import { IresDevice } from "@/data/models/IresDevice";
import { BranchFilter } from "@/data/models/BranchFilter";
import {
  DeviceFilter,
  matchesIresDeviceFilterWithContext,
  resolveIresDeviceFilterContext,
} from "@/data/models/DeviceFilter";
import User from "@/data/models/User";

import router from "@/router/router";
import { CockpitEntryInfo, CockpitEntryInfoType } from "./CockpitProjectsInfoCreator";

interface CalculateHyDryDeviceContext {
  vue: Vue;
  branchFilter: BranchFilter;
  targetUser: User | null;
}

function openDeviceList(
  deviceFilter: DeviceFilter,
  showAllDevices: boolean,
  context: CalculateHyDryDeviceContext
) {
  const { branchFilter } = context;

  router.push({
    path: "cockpit/devices",
    query: {
      showAllDevices: showAllDevices.toString(),
      deviceFilter: deviceFilter.toString(),
      ...(branchFilter.isSingleBranchFilter &&
        branchFilter.branch && { targetBranchId: branchFilter.branch.id }),
    },
  });
}

/**
 * Counts, in a single pass, how many devices match each of the given filters, plus
 * how many match the branch filter at all.
 *
 * One pass rather than one per counter, and the identity and capability state is
 * resolved once instead of per device per counter. Before, a master client's cockpit
 * evaluated six full passes over ~1000 devices, and every single evaluation re-read
 * session storage and re-parsed the whole user and client documents - tens of
 * thousands of `JSON.parse` per recompute, and it recomputes on every device
 * snapshot.
 */
function countIresDevicesPerFilter(
  filters: Array<DeviceFilter>,
  iresDevices: Array<IresDevice>,
  showAllDevices: boolean,
  context: CalculateHyDryDeviceContext
) {
  const { branchFilter, targetUser } = context;
  const filterContext = resolveIresDeviceFilterContext(targetUser);

  const counts = new Map<DeviceFilter, number>(
    filters.map((filter) => [filter, 0])
  );

  let numMatchingBranchFilter = 0;

  iresDevices.forEach((iresDevice) => {
    if (!branchFilter.doesMatchIresDevice(iresDevice)) {
      return;
    }

    numMatchingBranchFilter++;

    filters.forEach((filter) => {
      if (
        matchesIresDeviceFilterWithContext(
          filter,
          iresDevice,
          targetUser,
          filterContext,
          undefined,
          showAllDevices
        )
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        counts.set(filter, counts.get(filter)! + 1);
      }
    });
  });

  return {
    numMatchingBranchFilter,
    numberOf: (filter: DeviceFilter) => counts.get(filter) ?? 0,
  };
}

/**
 * Reads one counter out of the tiles by the filter it counts.
 *
 * Counters with a count of zero are not added at all, so the tiles cannot be indexed
 * positionally: index 0 is the in-usage counter only while at least one device is in usage,
 * and is the active counter otherwise.
 */
export function getHyDryDeviceCount(
  items: Array<CockpitEntryInfo>,
  deviceFilter: DeviceFilter
) {
  const match = items.find((item) => item.data?.deviceFilter === deviceFilter);

  return typeof match?.data?.count === "number" ? match.data.count : 0;
}

export function calculateHyDryDeviceItems(
  showAllDevices: boolean,
  iresDevices: IresDevice[],
  context: CalculateHyDryDeviceContext
) {
  if (iresDevices.length === 0) {
    return [];
  }

  let id = 0;
  const result: CockpitEntryInfo[] = [];

  // NOT_ASSIGNED_TO_PROJECT is only shown in the all-devices tile, so it is not
  // counted at all otherwise.
  const filters = [
    DeviceFilter.IN_USAGE,
    DeviceFilter.ALL_ACTIVE_DEVICES,
    DeviceFilter.ALL_FINISHED_DEVICES,
    DeviceFilter.OVERDUE_DEVICES,
    ...(showAllDevices ? [DeviceFilter.NOT_ASSIGNED_TO_PROJECT] : []),
  ];

  const counted = countIresDevicesPerFilter(
    filters,
    iresDevices,
    showAllDevices,
    context
  );

  const numInUsage = counted.numberOf(DeviceFilter.IN_USAGE);
  const numActive = counted.numberOf(DeviceFilter.ALL_ACTIVE_DEVICES);
  const numFinished = counted.numberOf(DeviceFilter.ALL_FINISHED_DEVICES);
  const numOverdue = counted.numberOf(DeviceFilter.OVERDUE_DEVICES);

  const { vue } = context;

  if (numInUsage) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.inUsage").toString(),
        count: numInUsage,
        deviceFilter: DeviceFilter.IN_USAGE,
      },
      onClickCallback: () => {
        openDeviceList(DeviceFilter.IN_USAGE, showAllDevices, context);
      },
    });
  }

  if (numActive) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.active").toString(),
        count: numActive,
      },
      onClickCallback: () => {
        openDeviceList(
          DeviceFilter.ALL_ACTIVE_DEVICES,
          showAllDevices,
          context
        );
      },
    });
  }

  if (showAllDevices) {
    const numNotAssigned = counted.numberOf(
      DeviceFilter.NOT_ASSIGNED_TO_PROJECT
    );

    if (numNotAssigned) {
      result.push({
        id: id++,
        shouldBeDisplayed: true,
        infoType: CockpitEntryInfoType.Counter,
        data: {
          name: vue.$t("deviceFilter.noAssignedToProject").toString(),
          count: numNotAssigned,
        },
        onClickCallback: () => {
          openDeviceList(
            DeviceFilter.NOT_ASSIGNED_TO_PROJECT,
            showAllDevices,
            context
          );
        },
      });
    }
  }

  if (numFinished) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.finished").toString(),
        count: numFinished,
      },
      onClickCallback: () => {
        openDeviceList(
          DeviceFilter.ALL_FINISHED_DEVICES,
          showAllDevices,
          context
        );
      },
    });
  }

  if (numOverdue) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.overdue").toString(),
        count: numOverdue,
      },
      onClickCallback: () => {
        openDeviceList(DeviceFilter.OVERDUE_DEVICES, showAllDevices, context);
      },
    });
  }

  if (showAllDevices) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.overall").toString(),
        count: counted.numMatchingBranchFilter,
      },
      onClickCallback: () => {
        openDeviceList(DeviceFilter.ALL_DEVICES, showAllDevices, context);
      },
    });
  }

  return result;
}
